import React, { useState, useEffect } from 'react';
import { Box, Modal, Typography } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useToast } from 'use-toast-mui';
import { FormInputDropdown } from '../../../../form-components/FormInputDropdown';
import { FormInputText } from '../../../../form-components/FormInputText';
import { FormInputPrice } from '../../../../form-components/FormInputPrice';
import { createBranchProduct, deleteProductById, updateBranchProduct } from '../../../../api/point';
import { errorMessages, formatterCurrency } from '../../../../utils/methods';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import CommonModalTitle from '../../../../form-components/CommonModalTitle';
import CommonText from '../../../../form-components/CommonText';
import CommonModalButton from '../../../../form-components/CommonModalButton';

// ============================================
// Animations
// ============================================
const errorPulseGlow = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.4);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(220, 38, 38, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(220, 38, 38, 0);
  }
`;

// ============================================
// Styled Components
// ============================================
const ErrorFieldWrapper = styled(Box, {
  shouldForwardProp: prop => prop !== 'hasError',
})(({ hasError }) => ({
  flex: 1,
  borderRadius: '4px',
  '& > *': {
    width: '100%',
  },
  ...(hasError && {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#dc2626 !important',
      borderWidth: '2px !important',
    },

    animation: `${errorPulseGlow} 1.5s ease-in-out infinite`,
  }),
}));

const ModalContainer = styled(Box)({
  backgroundColor: '#fff',
  borderRadius: '10px',
  width: '560px',
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
});

const ModalHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  color: '#fff',
  background: '#333',
  height: '52px',
  borderRadius: '10px 10px 0 0',
  padding: '0 20px',
});

const ModalTitle = styled(Typography)({
  fontSize: '16px',
  fontWeight: 500,
});

const StyledCloseIcon = styled(CloseIcon)({
  cursor: 'pointer',
});

const TabContainer = styled(Box)({
  display: 'flex',
  borderBottom: '1px solid #e0e0e0',
  padding: '20px 40px 0',
});

const TabItem = styled(Box, {
  shouldForwardProp: prop => prop !== 'isActive',
})(({ isActive }) => ({
  flex: 1,
  textAlign: 'center',
  paddingBottom: '12px',
  cursor: 'pointer',
  borderBottom: isActive ? '2px solid #4AB3BC' : '2px solid transparent',
}));

const TabText = styled(CommonText, {
  shouldForwardProp: prop => prop !== 'isActive',
})(({ isActive }) => ({
  fontSize: '18px',
  fontWeight: isActive ? 600 : 400,
  color: isActive ? '#4AB3BC' : '#666',
}));

const FormContainer = styled(Box)({
  padding: '24px 40px',
});

const FormRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  marginBottom: '16px',
});

const FormLabel = styled(CommonText)({
  width: '80px',
  flexShrink: 0,
});

const InputWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  flex: 1,
  gap: '8px',
});

const UnitText = styled(Typography)({
  fontSize: '14px',
  color: '#333',
});

const ModalFooter = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  gap: '12px',
  padding: '0 24px 24px 24px',
});

const inputStyle = {
  flex: 1,
  '& .MuiInputBase-root': {
    height: '40px',
    borderRadius: '4px',
  },
};

// ============================================
// Main Component
// ============================================
const ProductEditModal = ({ open, onClose, branchId, user, categories, activeProduct }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { handleSubmit, control, reset, setValue, watch } = useForm();
  const [productType, setProductType] = useState('STOCK');
  const [errorFields, setErrorFields] = useState({});

  const isEditMode = !!activeProduct?.productId;

  useEffect(() => {
    if (open) {
      if (activeProduct?.productId) {
        setProductType(activeProduct.productType || 'STOCK');
        reset({
          ...activeProduct,
          price: activeProduct.price ? formatterCurrency(activeProduct.price) : '',
          stockQuantity: activeProduct.stockQuantity ? formatterCurrency(activeProduct.stockQuantity) : '',
          periodUnit: activeProduct.periodType || 'MONTH',
        });
      } else {
        setProductType('STOCK');
        reset({});
      }
    }
  }, [open, activeProduct, reset]);

  // 필드 값 변경 시 해당 에러 제거
  const watchedFields = watch(['productName', 'categoryId', 'price', 'stockQuantity', 'periodValue', 'periodUnit']);
  useEffect(() => {
    const [productName, categoryId, price, stockQuantity, periodValue, periodUnit] = watchedFields;
    setErrorFields(prev => {
      const updated = { ...prev };
      if (productName) delete updated.productName;
      if (categoryId) delete updated.categoryId;
      if (price) delete updated.price;
      if (stockQuantity) delete updated.stockQuantity;
      if (periodValue) delete updated.periodValue;
      if (periodUnit) delete updated.periodUnit;
      return updated;
    });
  }, [watchedFields[0], watchedFields[1], watchedFields[2], watchedFields[3], watchedFields[4], watchedFields[5]]);

  const handleClose = () => {
    setProductType('STOCK');
    setErrorFields({});
    reset({});
    onClose();
  };

  const validateForm = values => {
    const errors = {};

    if (!values.productName) {
      errors.productName = true;
    }
    if (!values.categoryId) {
      errors.categoryId = true;
    }
    if (!values.price) {
      errors.price = true;
    }
    if (productType === 'STOCK' && !values.stockQuantity) {
      errors.stockQuantity = true;
    }
    if (productType === 'PERIOD' && !values.periodValue) {
      errors.periodValue = true;
    }
    if (productType === 'PERIOD' && !values.periodUnit) {
      errors.periodUnit = true;
    }

    setErrorFields(errors);

    if (Object.keys(errors).length > 0) {
      const firstError = Object.keys(errors)[0];
      const errorMsgs = {
        productName: '상품명을 입력해주세요',
        categoryId: '상품 분류를 선택해주세요',
        price: '상품 가격을 입력해주세요',
        stockQuantity: '재고 수량을 입력해주세요',
        periodValue: '유효 기간을 입력해주세요',
        periodUnit: '기간 단위를 선택해주세요',
      };
      toast.error(t(errorMsgs[firstError]));
      return false;
    }
    return true;
  };

  const buildRequestData = values => {
    const baseData = {
      productName: values.productName,
      categoryId: values.categoryId,
      price: Number(String(values.price).replace(/,/g, '')),
      productType: productType,
    };

    if (productType === 'STOCK') {
      return {
        ...baseData,
        quantity: Number(String(values.stockQuantity).replace(/,/g, '')),
        periodValue: null,
        periodType: null,
      };
    }

    // 기간 상품: 입력한 유효기간 그대로 사용
    return {
      ...baseData,
      periodValue: Number(values.periodValue),
      periodType: values.periodUnit || 'MONTH',
      quantity: null,
      subscriptionYn: false,
    };
  };

  // 상품 생성 mutation
  const createMutation = useMutation({
    mutationFn: requestData => createBranchProduct(requestData, branchId, user.token),
    onSuccess: () => {
      toast.success(t('상품이 등록되었습니다'));
      queryClient.invalidateQueries({ queryKey: ['categories', branchId] });
      queryClient.invalidateQueries({ queryKey: ['products', branchId] });
      handleClose();
    },
    onError: error => {
      toast.error(errorMessages(error));
    },
  });

  // 상품 수정 mutation
  const updateMutation = useMutation({
    mutationFn: requestData => updateBranchProduct(requestData, activeProduct.productId, user.token),
    onSuccess: () => {
      toast.success(t('상품이 수정되었습니다'));
      queryClient.invalidateQueries({ queryKey: ['categories', branchId] });
      queryClient.invalidateQueries({ queryKey: ['products', branchId] });
      handleClose();
    },
    onError: error => {
      toast.error(errorMessages(error));
    },
  });

  // 상품 삭제 mutation
  const deleteMutation = useMutation({
    mutationFn: () => deleteProductById(user.token, activeProduct.productId),
    onSuccess: () => {
      toast.success(t('상품이 삭제되었습니다'));
      queryClient.invalidateQueries({ queryKey: ['categories', branchId] });
      queryClient.invalidateQueries({ queryKey: ['products', branchId] });
      handleClose();
    },
    onError: error => {
      toast.error(errorMessages(error));
    },
  });

  const onSubmit = values => {
    if (!validateForm(values)) return;

    const requestData = buildRequestData(values);

    if (isEditMode) {
      updateMutation.mutate(requestData);
    } else {
      createMutation.mutate(requestData);
    }
  };

  const handleDelete = () => {
    deleteMutation.mutate();
  };

  const isLoading = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalContainer>
        {/* 헤더 */}
        <ModalHeader>
          <CommonModalTitle variant="large">{isEditMode ? t('상품 수정') : t('상품 등록')}</CommonModalTitle>
          <StyledCloseIcon onClick={handleClose} />
        </ModalHeader>

        {/* 탭 */}
        <TabContainer>
          <TabItem isActive={productType === 'STOCK'} onClick={() => setProductType('STOCK')}>
            <TabText isActive={productType === 'STOCK'}>{t('재고 상품')}</TabText>
          </TabItem>
          <TabItem isActive={productType === 'PERIOD'} onClick={() => setProductType('PERIOD')}>
            <TabText isActive={productType === 'PERIOD'}>{t('기간 상품')}</TabText>
          </TabItem>
        </TabContainer>

        {/* 폼 필드 */}
        <FormContainer>
          {/* 상품명 */}
          <FormRow>
            <FormLabel variant="label">{t('상품명')}</FormLabel>
            <ErrorFieldWrapper hasError={errorFields.productName}>
              <FormInputText
                control={control}
                sx={inputStyle}
                name="productName"
                placeholder={t('상품명을 입력해주세요.')}
              />
            </ErrorFieldWrapper>
          </FormRow>

          {/* 상품 분류 */}
          <FormRow>
            <FormLabel variant="label">{t('상품 분류')}</FormLabel>
            <ErrorFieldWrapper hasError={errorFields.categoryId}>
              <FormInputDropdown
                sx={inputStyle}
                name="categoryId"
                control={control}
                placeholder={t('상품 분류를 선택해주세요.')}
                options={categories?.map(category => ({
                  label: category.categoryName,
                  value: category.categoryId,
                }))}
              />
            </ErrorFieldWrapper>
          </FormRow>

          {/* 상품 가격 */}
          <FormRow>
            <FormLabel variant="label">{t('상품 가격')}</FormLabel>
            <InputWrapper>
              <ErrorFieldWrapper hasError={errorFields.price}>
                <FormInputPrice
                  control={control}
                  setValue={setValue}
                  sx={inputStyle}
                  name="price"
                  placeholder={t('상품 가격을 입력해주세요.')}
                />
              </ErrorFieldWrapper>
              <UnitText>{t('원')}</UnitText>
            </InputWrapper>
          </FormRow>

          {/* 재고 수량 (재고 상품일 때) */}
          {productType === 'STOCK' && (
            <FormRow>
              <FormLabel variant="label">{t('재고 수량')}</FormLabel>
              <InputWrapper>
                <ErrorFieldWrapper hasError={errorFields.stockQuantity}>
                  <FormInputPrice
                    control={control}
                    setValue={setValue}
                    sx={inputStyle}
                    name="stockQuantity"
                    placeholder={t('재고 수량을 입력해주세요.')}
                  />
                </ErrorFieldWrapper>
                <UnitText>{t('개')}</UnitText>
              </InputWrapper>
            </FormRow>
          )}

          {/* 유효 기간 (기간 상품일 때) */}
          {productType === 'PERIOD' && (
            <FormRow>
              <FormLabel variant="label">{t('유효 기간')}</FormLabel>
              <InputWrapper>
                <ErrorFieldWrapper hasError={errorFields.periodValue}>
                  <FormInputText
                    control={control}
                    sx={inputStyle}
                    name="periodValue"
                    type="number"
                    placeholder={t('유효 기간을 입력해주세요.')}
                  />
                </ErrorFieldWrapper>
                <ErrorFieldWrapper hasError={errorFields.periodUnit} style={{ flex: 'none', width: '80px' }}>
                  <FormInputDropdown
                    sx={{
                      width: '80px',
                      '& .MuiInputBase-root': {
                        height: '40px',
                        borderRadius: '4px',
                      },
                    }}
                    name="periodUnit"
                    control={control}
                    defaultValue="MONTH"
                    options={[
                      { label: t('개월'), value: 'MONTH' },
                      { label: t('일'), value: 'DAY' },
                    ]}
                  />
                </ErrorFieldWrapper>
              </InputWrapper>
            </FormRow>
          )}
        </FormContainer>

        {/* 버튼 */}
        <ModalFooter>
          <CommonModalButton variant="cancel" onClick={handleClose}>
            {t('취소')}
          </CommonModalButton>
          {isEditMode && (
            <CommonModalButton variant="delete" onClick={handleDelete} disabled={isLoading}>
              {t('삭제')}
            </CommonModalButton>
          )}
          <CommonModalButton variant="submit" onClick={handleSubmit(onSubmit)} disabled={isLoading}>
            {isEditMode ? t('저장') : t('등록')}
          </CommonModalButton>
        </ModalFooter>
      </ModalContainer>
    </Modal>
  );
};

export default ProductEditModal;
